import { useEffect, useState, ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import { parseDomain } from '@/lib/utils/subdomain';
import { supabase } from '@/lib/supabase';
import {
  appContextTypeAtom,
  appSubdomainIdentifierAtom,
  isCustomDomainAtom,
  currentCommunityAtom,
  communityLoadingAtom,
  communityErrorAtom,
} from '@/lib/stores/community';
import { routerLogger } from '@/lib/logger';
import type { Community } from '@/types/db';

// Simplified feature initialization function (replaces initializeCommunityModulesAsync)
// Uses simple feature flags instead of complex module system
const initializeCommunityFeaturesAsync = async (communityId: string) => {
  try {
    // Get features selection from localStorage first, then fallback to community settings
    let features = null;
    const featuresData = localStorage.getItem('features_selection');

    if (featuresData) {
      features = JSON.parse(featuresData);
    } else {
      // Fallback: read from community settings
      console.log(
        '[FEATURE INIT] No localStorage features found, checking community settings...'
      );
      const { data: communityData, error: communityError } = await supabase
        .from('communities')
        .select('settings')
        .eq('id', communityId)
        .single();

      if (!communityError && communityData?.settings?.featuresSelection) {
        features = communityData.settings.featuresSelection;
        console.log('[FEATURE INIT] Found features in community settings:', features);
      }
    }

    // Default fallback
    if (!features) {
      features = {
        enableEvents: true,
        enableChannels: true,
        jobsVisibility: 'public_teaser',
      };
    }

    console.log(
      '[FEATURE INIT] Starting initialization for community:',
      communityId,
      'with features:',
      features
    );

    // Check if features already initialized to prevent duplicates
    const { data: existingCommunity, error: checkError } = await supabase
      .from('communities')
      .select('show_events, show_channels')
      .eq('id', communityId)
      .single();

    if (checkError) {
      console.error('[FEATURE INIT] Error checking existing features:', checkError);
      throw new Error(
        `Failed to check existing features: ${checkError.message}`
      );
    }

    if (existingCommunity && existingCommunity.show_events !== null) {
      console.log(
        '[FEATURE INIT] Features already initialized, skipping...'
      );
      localStorage.removeItem('features_selection');
      return;
    }

    console.log(
      '[FEATURE INIT] No existing features found, proceeding with initialization...'
    );

    // Update community with feature flags (single UPDATE)
    const { error: updateError } = await supabase
      .from('communities')
      .update({
        show_events: features.enableEvents ?? true,
        show_channels: features.enableChannels ?? true,
        jobs_visibility: features.jobsVisibility || 'public_teaser',
        events_visibility: features.eventsVisibility || 'public_full',
        channels_visibility: features.channelsVisibility || 'members_only',
      })
      .eq('id', communityId);

    if (updateError) {
      console.error('[FEATURE INIT] Failed to initialize features:', updateError);
      throw new Error(
        `Failed to initialize features: ${updateError.message}`
      );
    }

    // Clean up localStorage
    localStorage.removeItem('features_selection');

    console.log(
      '[FEATURE INIT] ✅ SUCCESS! Features initialized for community:',
      communityId
    );
    console.log('[FEATURE INIT] - Events enabled:', features.enableEvents);
    console.log('[FEATURE INIT] - Channels enabled:', features.enableChannels);
    console.log('[FEATURE INIT] - Jobs visibility:', features.jobsVisibility);
  } catch (error) {
    console.error(
      '[FEATURE INIT] ❌ FAILED to initialize features for:',
      communityId,
      'Error:',
      error
    );
  }
};

// Make initialization function globally available for debugging/recovery
(window as any).retryFeatureInit = async (communityId: string) => {
  console.log(
    '[FEATURE INIT] Manual retry triggered for community:',
    communityId
  );
  await initializeCommunityFeaturesAsync(communityId);
};

interface AppContextProviderProps {
  children: ReactNode;
}

/**
 * AI Context: AppContextProvider Component
 *
 * Purpose: Determines the application context (platform, community, app) based on
 *          hostname (prod) or query parameters (dev) using parseDomain().
 *          Fetches community data if context is 'community' and updates Jotai state.
 * Location: src/components/layout/AppContextProvider.tsx
 * Usage: Wrap the main RouterProvider or App component in App.tsx.
 */
export function AppContextProvider({ children }: AppContextProviderProps) {
  // Use state to ensure parseDomain runs only once on mount client-side
  const [isInitialized, setIsInitialized] = useState(false);

  // Jotai setters
  const setAppContextType = useSetAtom(appContextTypeAtom);
  const setAppSubdomainIdentifier = useSetAtom(appSubdomainIdentifierAtom);
  const setIsCustomDomain = useSetAtom(isCustomDomainAtom);
  const setCurrentCommunity = useSetAtom(currentCommunityAtom);
  const setCommunityLoading = useSetAtom(communityLoadingAtom);
  const setCommunityError = useSetAtom(communityErrorAtom);

  useEffect(() => {
    // Run only once after mount
    if (typeof window === 'undefined' || isInitialized) {
      return;
    }
    setIsInitialized(true);

    const { subdomain, type, isCustomDomain } = parseDomain();

    // Update basic context atoms
    setAppContextType(type);
    setAppSubdomainIdentifier(subdomain);
    setIsCustomDomain(isCustomDomain);

    // If community context, fetch community data
    if (type === 'community' && subdomain) {
      const fetchCommunityData = async (retryCount = 0) => {
        setCommunityLoading(true);
        setCommunityError(null);
        setCurrentCommunity(null); // Clear previous community data

        try {
          // Determine the lookup column based on whether it's a custom domain
          const lookupColumn = isCustomDomain ? 'custom_domain' : 'slug';

          const { data, error } = await supabase
            .from('communities') // Ensure this table name is correct
            .select('*') // Select desired columns
            .eq(lookupColumn, subdomain)
            .single();

          if (error) {
            if (error.code === 'PGRST116') {
              // Community not found - check if this might be a timing issue from recent onboarding
              const urlParams = new URLSearchParams(window.location.search);
              const urlFlag = urlParams.get('init_modular') === 'true';
              const localStorageFlag =
                localStorage.getItem('onboarding_complete') === 'true';
              const isPostOnboarding = urlFlag || localStorageFlag;
              const maxRetries = 3;

              if (isPostOnboarding && retryCount < maxRetries) {
                console.log(
                  `Community not found, retrying (${retryCount + 1}/${maxRetries}) in ${(retryCount + 1) * 1000}ms...`
                );
                setTimeout(
                  () => fetchCommunityData(retryCount + 1),
                  (retryCount + 1) * 1000
                );
                return; // Don't set loading to false yet
              } else {
                setCommunityError('Community not found.');
              }
            } else {
              setCommunityError(`Error loading community: ${error.message}`);
            }
            setCurrentCommunity(null);
          } else if (data) {
            setCurrentCommunity(data as Community); // Ensure type assertion is safe
            setCommunityError(null);

            // If we found the community after onboarding completion, initialize modules
            // Check both localStorage (old method) and URL parameter (new method for cross-subdomain)
            const urlParams = new URLSearchParams(window.location.search);
            const urlFlag = urlParams.get('init_modular') === 'true';
            const localStorageFlag =
              localStorage.getItem('onboarding_complete') === 'true';
            const isPostOnboarding = urlFlag || localStorageFlag;

            console.log('[MODULAR INIT] Checking post-onboarding flags:', {
              urlFlag,
              localStorageFlag,
              isPostOnboarding,
              currentUrl: window.location.href,
            });

            if (isPostOnboarding) {
              console.log(
                '[FEATURE INIT] 🚀 Post-onboarding community load detected, initializing features for community:',
                data.id
              );
              initializeCommunityFeaturesAsync(data.id);

              // Clean up flags
              localStorage.removeItem('onboarding_complete');
              if (urlFlag) {
                // Remove the URL parameter by updating the URL without page reload
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('init_modular');
                window.history.replaceState({}, '', newUrl.toString());
                console.log(
                  '[FEATURE INIT] Cleaned up init_modular URL parameter'
                );
              }
            } else {
              console.log(
                '[FEATURE INIT] No post-onboarding flags detected, skipping initialization'
              );
            }
          } else {
            setCommunityError('Community not found.');
            setCurrentCommunity(null);
          }
        } catch (err: unknown) {
          const errorMessage =
            err instanceof Error ? err.message : 'Unknown error';
          setCommunityError(`Failed to load community: ${errorMessage}`);
          setCurrentCommunity(null);
        } finally {
          setCommunityLoading(false);
        }
      };

      fetchCommunityData();
    } else {
      // Not a community context, ensure loading is false and no community data
      setCommunityLoading(false);
      setCurrentCommunity(null);
      setCommunityError(null);
      if (type !== 'platform') {
        // If it's not platform or community, it's the base 'app' type
        setAppContextType('app');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]); // Run only once

  // Render children while context is being determined/fetched
  // Consider adding a loading state here if needed before rendering children,
  // but often it's better handled downstream by components consuming the context.
  return <>{children}</>;
}
